<script>
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import { mapGetters } from 'vuex';
import getUuid from 'widget/helpers/uuid';
import { regenerateStepIds } from '../../helpers/automationUtils';
export default {
  components: {
    ButtonNext,
    IconNext,
  },
  inject: {
    stepAncestorIds: { default: () => [] },
    forEachMaxIterationsCap: { default: 500 },
  },
  emits: ['close', 'add', 'delete'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      toLocale,
    };
  },
  data() {
    return {
      search: '',
    };
  },
  computed: {
    ...mapGetters({
      registryActions: 'wgptAutomationRegistry/getActions',
      actionClipboard: 'wgptAutomations/getActionClipboard',
    }),
    actions() {
      const actions = this.registryActions;
      const filteredActions = actions.filter(item => !item.hidden);
      const actionNames = filteredActions.map(({ action }) => action);
      const baseActionNames = actionNames.map(actionName => {
        const parts = actionName.split('_');
        const versionPart = parts[parts.length - 1];
        const hasVersion = /^v\d+$/.test(versionPart);
        return hasVersion ? parts.slice(0, -1).join('_') : actionName;
      });
      const uniqueBaseActionNames = baseActionNames.filter(
        (actionName, index) => baseActionNames.indexOf(actionName) === index
      );
      const latestVersionActions = uniqueBaseActionNames.map(actionName => {
        const actionRegex = new RegExp(`^${actionName}(_v\\d+)?$`);
        const versions = actions.filter(({ action }) =>
          actionRegex.test(action)
        );
        const latestVersion = versions.reduce(
          (latest, current) => {
            if (current.version > latest.version) {
              return current;
            }
            return latest;
          },
          { version: 0 }
        );
        return latestVersion;
      });
      return latestVersionActions;
    },
    filteredActions() {
      const query = this.search.toLowerCase();
      if (!query) return this.actions;
      return this.actions.filter(action => {
        return this.toLocale(action.name).toLowerCase().includes(query);
      });
    },
    showBranch() {
      const query = this.search.toLowerCase();
      if (!query) return true;
      return this.toLocale('ACTIONS.BRANCH.NAME').toLowerCase().includes(query);
    },
    showForEach() {
      if (this.forEachMaxIterationsCap <= 0) return false;
      const query = this.search.toLowerCase();
      if (!query) return true;
      return this.toLocale('ACTIONS.FOR_EACH.NAME')
        .toLowerCase()
        .includes(query);
    },
    noResults() {
      return (
        !this.showBranch &&
        !this.showForEach &&
        this.filteredActions.length === 0 &&
        this.search
      );
    },
    hasActionClipboard() {
      const actionClipboard = this.actionClipboard;
      if (!actionClipboard) return false;

      const { action: clipboardAction } = actionClipboard;

      // Prevent pasting a cut for_each/branch inside itself (at any nesting level)
      if (
        actionClipboard.operation === 'cut' &&
        this.stepAncestorIds.includes(clipboardAction.id)
      ) {
        return false;
      }

      if (['branch', 'for_each'].includes(clipboardAction.action)) return true;

      return this.registryActions.some(
        registryAction => registryAction.action === clipboardAction.action
      );
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.searchbar?.focus();
    });
  },
  methods: {
    addBranch() {
      const step = {
        id: getUuid(),
        action: 'branch',
        branches: [
          {
            id: getUuid(),
            name: this.$t(
              'WGPT_AUTOMATIONS.EDITOR.BRANCH.FALLBACK_BRANCH_NAME',
              {
                index: 1,
              }
            ),
            logical_operator: 'AND',
            conditions: [
              {
                variable_name: '',
                operator: 'equals',
                value: '',
              },
            ],
            steps: [],
          },
        ],
        default: {
          id: getUuid(),
          steps: [],
        },
      };

      this.$emit('close');
      this.$emit('add', step);
      this.$router.replace({ query: { selected: step.id } }).catch(() => {});
    },
    addForEach() {
      const step = {
        id: getUuid(),
        action: 'for_each',
        attributes: {
          collection: '',
          max_iterations: this.forEachMaxIterationsCap,
          field_mapping: [
            { variable_name: 'item', type: 'text', json_key: '$' },
          ],
        },
        steps: [],
      };

      this.$emit('close');
      this.$emit('add', step);
      this.$router.replace({ query: { selected: step.id } }).catch(() => {});
    },
    addAction(action) {
      const step = {
        id: getUuid(),
        action: action.action,
      };

      const formFields = action.form_fields ?? [];
      formFields.forEach(field => {
        if (Object.keys(field).includes('default')) {
          const stepAttributesBefore = step.attributes ?? {};
          step.attributes = {
            ...stepAttributesBefore,
            [field.name]: field.default,
          };
        }
      });

      const outputMappingFields = action.output_mapping_fields ?? [];
      outputMappingFields.forEach(field => {
        if (Object.keys(field).includes('default')) {
          const stepOutputMappingBefore = step.output_mapping ?? {};
          step.output_mapping = {
            ...stepOutputMappingBefore,
            [field.name]: field.default,
          };
        }
      });

      this.$emit('close');
      this.$emit('add', step);
      this.$router.replace({ query: { selected: step.id } }).catch(() => {});
    },
    async pasteStep() {
      const { action, operation } = await this.$store.dispatch(
        'wgptAutomations/pasteAction'
      );
      if (!action) return;

      const newStep = regenerateStepIds(action);
      const newId = newStep.id;
      this.$emit('add', newStep);
      this.$emit('close');
      if (operation === 'cut') {
        this.$emit('delete', action.id);
      }

      this.$nextTick(() => {
        this.$router.replace({ query: { selected: newId } });
      });
    },
  },
};
</script>

<template>
  <div class="min-w-[300px]">
    <div class="relative border-b border-n-strong">
      <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
      <input
        ref="searchbar"
        v-model="search"
        type="search"
        :placeholder="
          $t('WGPT_AUTOMATIONS.EDITOR.ADD_STEP_DROPDOWN.SEARCH_PLACEHOLDER')
        "
        class="reset-base outline-none w-full py-2 pl-10 pr-2 text-sm border-none rounded-t-md bg-n-solid-1 text-slate-900 dark:text-slate-50"
      />
      <div class="absolute end-1 top-1 flex">
        <ButtonNext
          v-if="hasActionClipboard"
          color="slate"
          variant="ghost"
          size="xs"
          icon="i-lucide-clipboard-paste"
          @click="pasteStep"
        />
      </div>
    </div>

    <div class="py-2 w-full max-h-60 overflow-auto">
      <ul class="py-1 mb-0" role="listbox">
        <li
          v-if="showBranch"
          class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
          role="option"
          @click="addBranch()"
        >
          <span class="text-n-slate-12">
            {{ toLocale('ACTIONS.BRANCH.NAME') }}
          </span>
          <IconNext
            v-tooltip.top="{
              content: toLocale('ACTIONS.BRANCH.DESCRIPTION'),
              popperClass: 'wgpt-automations-add-step-dropdown-item-tooltip',
              container: false,
              boundary: 'body',
            }"
            icon="i-lucide-info"
            class="shrink-0"
            size="14"
          />
        </li>
        <li
          v-if="showForEach"
          class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
          role="option"
          @click="addForEach()"
        >
          <span class="text-n-slate-12">
            {{ toLocale('ACTIONS.FOR_EACH.NAME') }}
          </span>
          <IconNext
            v-tooltip.top="{
              content: toLocale('ACTIONS.FOR_EACH.DESCRIPTION'),
              popperClass: 'wgpt-automations-add-step-dropdown-item-tooltip',
              container: false,
              boundary: 'body',
            }"
            icon="i-lucide-info"
            class="shrink-0"
            size="14"
          />
        </li>
        <li
          v-for="action of filteredActions"
          :key="action.action"
          class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
          role="option"
          @click="addAction(action)"
        >
          <span class="text-n-slate-12">
            {{ toLocale(action.name) }}
          </span>
          <IconNext
            v-if="action.description"
            v-tooltip.top="{
              content: toLocale(action.description),
              popperClass: 'wgpt-automations-add-step-dropdown-item-tooltip',
              container: false,
              boundary: 'body',
            }"
            icon="i-lucide-info"
            class="shrink-0"
            size="14"
          />
        </li>
      </ul>
      <p v-if="noResults" class="px-3 py-2 mb-0 text-sm text-n-slate-11">
        {{ $t('WGPT_AUTOMATIONS.EDITOR.ADD_STEP_DROPDOWN.NO_RESULTS') }}
      </p>
    </div>
  </div>
</template>

<style lang="scss">
.wgpt-automations-add-step-dropdown-item-tooltip {
  max-width: 300px;
  white-space: wrap;
}
</style>
